import React, { useState, useRef } from "react";
import { IoArrowUpOutline } from "react-icons/io5";
import Progress from "../progress";
import { collectMonthlyExtraWork } from "../../projects/workDetailsForm/workTypeMapping";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Work Details Tooltip Component
const WorkDetailsTooltip = ({ project, monthKey, position = "top" }) => {
  let monthlyDetails = null;

  // If monthKey is provided, find the specific month's work details
  if (monthKey && project?.workDetails) {
    monthlyDetails = project.workDetails.find(
      (details) => details.month === monthKey
    );
  }

  // If no month-specific details found, try to use aggregated work details
  if (!monthlyDetails && project?.aggregatedWorkDetails) {
    // Create a pseudo monthlyDetails object from aggregated data
    monthlyDetails = {
      reels: project.aggregatedWorkDetails.reels,
      poster: project.aggregatedWorkDetails.poster,
      motionPoster: project.aggregatedWorkDetails.motionPoster,
      shooting: project.aggregatedWorkDetails.shooting,
      motionGraphics: project.aggregatedWorkDetails.motionGraphics,
      other: project.aggregatedWorkDetails.other || [],
    };
  }

  // If still no details, aggregate all months in workDetails array to show overall progress
  if (
    !monthlyDetails &&
    project?.workDetails &&
    project.workDetails.length > 0
  ) {
    const aggregated = {
      reels: { count: 0, total: 0, extra: 0 },
      poster: { count: 0, total: 0, extra: 0 },
      motionPoster: { count: 0, total: 0, extra: 0 },
      shooting: { count: 0, total: 0, extra: 0 },
      motionGraphics: { count: 0, total: 0, extra: 0 },
      other: [],
    };

    project.workDetails.forEach((month) => {
      ["reels", "poster", "motionPoster", "shooting", "motionGraphics"].forEach((type) => {
        if (month[type]) {
          aggregated[type].count += month[type].count || 0;
          aggregated[type].total += month[type].total || 0;
          aggregated[type].extra += month[type].extra || 0;
        }
      });
      if (month.other && Array.isArray(month.other)) {
        month.other.forEach((item) => {
          if (item && item.name) {
            const existing = aggregated.other.find((o) => o.name === item.name);
            if (existing) {
              existing.count += item.count || 0;
              existing.total += item.total || 0;
              existing.extra += item.extra || 0;
            } else {
              aggregated.other.push({
                name: item.name,
                count: item.count || 0,
                total: item.total || 0,
                extra: item.extra || 0,
              });
            }
          }
        });
      }
    });

    monthlyDetails = aggregated;
  }

  if (!monthlyDetails) {
    return null;
  }

  // Helper function to format work type data
  const formatWorkType = (workType, label) => {
    if (!workType || workType.total === 0) return null;
    const total = workType.total || 0;
    const remaining = workType.count || 0; // Pending/remaining items
    // Calculate completed: total - remaining (matches backend calculation)
    const completed = total - remaining;
    const progress = total > 0 ? Math.round((completed / total) * 100) : 0;

    return {
      label,
      total,
      completed,
      remaining,
      progress,
      extra: workType.extra || 0,
    };
  };

  // Collect all work types
  const workTypes = [
    formatWorkType(monthlyDetails.reels, "Reels"),
    formatWorkType(monthlyDetails.poster, "Poster"),
    formatWorkType(monthlyDetails.motionPoster, "Motion Poster"),
    formatWorkType(monthlyDetails.shooting, "Shooting"),
    formatWorkType(monthlyDetails.motionGraphics, "Motion Graphics"),
  ].filter(Boolean);

  // Add other work types
  if (monthlyDetails.other && Array.isArray(monthlyDetails.other)) {
    monthlyDetails.other.forEach((item) => {
      if (item && item.total > 0) {
        const total = item.total || 0;
        const remaining = item.count || 0; // Pending/remaining items
        // Calculate completed: total - remaining (matches backend calculation)
        const completed = total - remaining;
        const progress = total > 0 ? Math.round((completed / total) * 100) : 0;
        workTypes.push({
          label: item.name || "Other",
          total,
          completed,
          remaining,
          progress,
          extra: item.extra || 0,
        });
      }
    });
  }

  const extraWork = collectMonthlyExtraWork(monthlyDetails);

  if (workTypes.length === 0 && extraWork.length === 0) {
    return null;
  }

  // Format month name
  let monthName = "Overall";
  if (monthKey) {
    try {
      const [year, month] = monthKey.split("-");
      monthName = new Date(year, parseInt(month) - 1, 1).toLocaleDateString(
        "en-US",
        {
          month: "long",
          year: "numeric",
        }
      );
    } catch (error) {
      monthName = "Overall";
    }
  }

  // Position classes based on position prop
  const positionClasses =
    position === "bottom"
      ? "top-full right-0 mt-3" // Show below, align to right
      : "bottom-full right-0 mb-3"; // Show above (default), align to right

  const arrowClasses =
    position === "bottom"
      ? "top-0 right-6 transform -translate-y-full" // Arrow pointing up, aligned to right
      : "bottom-0 right-6 transform translate-y-full"; // Arrow pointing down (default), aligned to right

  return (
    <div
      className={`absolute ${positionClasses} w-64 bg-white rounded-lg shadow-xl border border-gray-200 p-4 z-50 pointer-events-none transition-opacity duration-200`}
    >
      <div className={`absolute ${arrowClasses}`}>
        {position === "bottom" ? (
          // Arrow pointing up
          <>
            <div className="border-4 border-transparent border-b-gray-200"></div>
            <div className="border-4 border-transparent border-b-white -mt-1"></div>
          </>
        ) : (
          // Arrow pointing down (default)
          <>
            <div className="border-4 border-transparent border-t-gray-200"></div>
            <div className="border-4 border-transparent border-t-white -mt-1"></div>
          </>
        )}
      </div>
      <div className="mb-2 pb-2 border-b border-gray-200">
        <h6 className="font-semibold text-sm text-gray-800">Work Details</h6>
        <p className="text-xs text-gray-500 mt-0.5">{monthName}</p>
      </div>
      <div className="space-y-3 max-h-64 overflow-y-auto">
        {workTypes.map((workType, index) => (
          <div key={index} className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-800">
                {workType.label}
              </span>
              <span className="text-xs font-medium text-gray-600">
                {workType.completed}/{workType.total}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 transition-all duration-300"
                  style={{ width: `${workType.progress}%` }}
                />
              </div>
              <span className="text-xs font-semibold text-gray-700 min-w-[40px] text-right">
                {workType.progress}%
              </span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-green-600 font-medium">
                ✓ {workType.completed} completed
              </span>
              {workType.remaining > 0 && (
                <span className="text-orange-600 font-medium">
                  ⏳ {workType.remaining} pending
                </span>
              )}
              {workType.remaining === 0 && (
                <span className="text-gray-400">All done</span>
              )}
            </div>
            {Number(workType.extra) > 0 && (
              <span className="text-[10px] font-semibold text-amber-600">
                +{workType.extra} extra
              </span>
            )}
          </div>
        ))}
        {extraWork.length > 0 && (
          <div className="pt-2 mt-1 border-t border-amber-100">
            <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wide mb-1.5">
              Extra work
            </p>
            {extraWork.map((item) => (
              <div
                key={`${item.kind}-${item.key || item.name}`}
                className="flex items-center justify-between text-xs py-0.5"
              >
                <span className="text-gray-700">{item.name}</span>
                <span className="font-semibold text-amber-600">+{item.extra}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ShimmerBox = ({ className = "" }) => (
  <div
    className={`bg-slate-200 animate-shimmer bg-[linear-gradient(110deg,#e2e8f0,45%,#f1f5f9,55%,#e2e8f0)] bg-[length:200%_100%] rounded ${className}`}
  />
);

export const ProjectCardShimmer = () => (
  <div className="bg-white rounded-[24px] grid gap-y-4 md:gap-y-0 md:grid-cols-2">
    <div className="p-4 py-5 h-full flex gap-y-4 flex-col md:border-r border-[#E4E6E8]/60">
      <div className="flex items-center gap-x-3.5">
        <ShimmerBox className="w-10 h-10 rounded-2xl shrink-0" />
        <div className="flex flex-col gap-y-2 flex-1 min-w-0">
          <ShimmerBox className="h-3 w-16" />
          <ShimmerBox className="h-4 w-3/4" />
        </div>
      </div>
      <div className="flex items-center justify-between">
        <ShimmerBox className="h-3 w-32" />
        <ShimmerBox className="h-3 w-12" />
      </div>
    </div>
    <div className="px-8 py-5 flex flex-col gap-y-3 justify-center">
      <ShimmerBox className="h-4 w-24" />
      <div className="grid grid-cols-3 gap-3">
        <div className="flex flex-col gap-y-2">
          <ShimmerBox className="h-3 w-16" />
          <ShimmerBox className="h-6 w-10" />
        </div>
        <div className="flex flex-col gap-y-2">
          <ShimmerBox className="h-3 w-16" />
          <ShimmerBox className="h-6 w-10" />
        </div>
        <div className="flex flex-col gap-y-2">
          <ShimmerBox className="h-3 w-16" />
          <div className="flex -space-x-2">
            <ShimmerBox className="h-6 w-6 rounded-full" />
            <ShimmerBox className="h-6 w-6 rounded-full" />
            <ShimmerBox className="h-6 w-6 rounded-full" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ProjectCard = ({ project, onClick, viewMore = false }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [showProgressTooltip, setShowProgressTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState("top");
  const [failedImages, setFailedImages] = useState(new Set());
  const progressRef = useRef(null);

  const handleImageError = (index) => {
    setFailedImages((prev) => new Set([...prev, index]));
  };

  const getTeamMemberName = (team) => {
    return team?.name || team?.firstName || `${team?.firstName || ""} ${team?.lastName || ""}`.trim() || "U";
  };

  const getFirstLetter = (team) => {
    const name = getTeamMemberName(team);
    return name.charAt(0).toUpperCase();
  };

  return (
    <div
      key={project?._id}
      className="flex flex-col cursor-pointer   h-fit "
      onClick={onClick}
    >
      <div
        className={`bg-white   rounded-[24px] grid gap-y-4 md:gap-y-0 md:grid-cols-2`}
      >
        <div
          className="p-4  py-5 h-full flex gap-y-4 flex-col  md:border-r 
       relative border-[#E4E6E8]/60 "
        >
          <div className="flexStart  gap-x-3.5">
            <div className="w-10 h-10 rounded-2xl overflow-hidden">
              <img
                src={project?.thumbImg}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex flex-col h-full justify-center">
              <span className="text-[11px] uppercase text-[#91929E]">
                {project?._id?.slice(0, 8)}
              </span>
              <h4 className="font-medium text-sm text-gray-800">{project?.name}</h4>
            </div>
          </div>
          <div className="flexBetween">
            <div className="flexStart gap-x-4">
              <div className="flexStart gap-x-2">
                <img src="/icons/calender2.svg" alt="" loading="lazy" className="w-4" />
                <span className="text-[11px] text-[#7D8592]">
                  Created {formatDate(project?.createdAt)}
                </span>
              </div>
              {viewMore && (
                <div className="flexStart gap-x-2">
                  <img src="/icons/calender2.svg" alt="" loading="lazy" className="w-4" />
                  <span className="text-[11px] text-[#7D8592]">
                    Ended {formatDate(project?.endDate)}
                  </span>
                </div>
              )}
            </div>
            <div className="flexEnd text-[#FFBD21] text-[11px] gap-x-1.5 pr-2">
              <IoArrowUpOutline className="text-base" />
              <span>{project?.priority}</span>
            </div>
          </div>
          <div className="absolute  right-5 top-5 w-fit">
            <div
              ref={progressRef}
              className="relative"
              onMouseEnter={() => {
                // Calculate position when hovering
                if (progressRef.current) {
                  const rect = progressRef.current.getBoundingClientRect();
                  const viewportHeight = window.innerHeight;
                  const spaceAbove = rect.top;
                  const spaceBelow = viewportHeight - rect.bottom;
                  const tooltipHeight = 300; // Approximate tooltip height

                  // If there's more space below and enough space, show below
                  // Otherwise show above
                  if (spaceBelow > tooltipHeight || spaceBelow > spaceAbove) {
                    setTooltipPosition("bottom");
                  } else {
                    setTooltipPosition("top");
                  }
                }
                setShowProgressTooltip(true);
              }}
              onMouseLeave={() => setShowProgressTooltip(false)}
            >
              <Progress
                size={40}
                strokeWidth={3}
                currentValue={project?.progress}
              />
              <span
                className="text-[11px] absolute top-1/2 left-1/2 -translate-x-1/2
               -translate-y-1/2 text-gray-500 font-semibold"
              >
                {project?.progress}%
              </span>
              {showProgressTooltip && (
                <WorkDetailsTooltip
                  project={project}
                  monthKey={project?.monthKey || null}
                  position={tooltipPosition}
                />
              )}
            </div>
          </div>
        </div>
        <div className="px-8  py-5   flex flex-col  gap-y-3 justify-center items-center">
          <h5 className="font-medium text-sm w-full">Project Data</h5>
          <div className="w-full grid grid-cols-3 ">
            <div className="flex flex-col gap-y-2">
              <span className="text-[#91929E]/90 text-xs">All Tasks</span>
              <span className="font-semibold text-gray-800 text-base">
                {project?.monthTasks !== undefined
                  ? project.monthTasks
                  : project?.tasks?.length || 0}
              </span>
            </div>
            <div className="flex flex-col gap-y-2">
              <span className="text-[#91929E]/90 text-xs">Active tasks</span>
              <span className="font-semibold text-gray-800 text-base">
                {project?.monthActiveTasks !== undefined
                  ? project.monthActiveTasks
                  : project?.tasks?.length || 0}
              </span>
            </div>
            <div className="flex flex-col    gap-y-2">
              <span className="text-[#91929E]/90 text-xs">Assignees</span>
              <div className="font-semibold flex items-center text-gray-800 text-base relative isolate">
                {project?.teams?.slice(0, 5).map((team, index) => (
                  <div
                    key={index}
                    className="relative group"
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    style={{
                      marginLeft: index > 0 ? "-8px" : "0",
                      zIndex:
                        hoveredIndex === index
                          ? 100
                          : project?.teams?.length - index,
                    }}
                  >
                    <div
                      className={`w-6 h-6 rounded-full overflow-hidden border-2 border-white relative transition-transform duration-200 ease-in-out ${hoveredIndex === index ? "scale-125" : "scale-100"
                        }`}
                    >
                      {!failedImages.has(index) && team?.profileImage ? (
                        <img
                          src={team?.profileImage}
                          alt={getTeamMemberName(team)}
                          loading="lazy"
                          className="w-full h-full object-cover"
                          onError={() => handleImageError(index)}
                        />
                      ) : (
                        <div className="w-full h-full rounded-full bg-gray-200 flexCenter text-gray-800 font-semibold text-[10px]">
                          {getFirstLetter(team)}
                        </div>
                      )}
                    </div>
                    {hoveredIndex === index && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-[10px] rounded whitespace-nowrap pointer-events-none z-50">
                        {getTeamMemberName(team)}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1">
                          <div className="border-4 border-transparent border-t-gray-900"></div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {project?.teams?.length > 5 && (
                  <div
                    style={{
                      marginLeft: "-8px",
                      zIndex: 100,
                    }}
                    className={`min-w-6 min-h-6  flex text-[8px]
                      text-white items-center justify-center rounded-full
                      bg-gray-600  overflow-hidden border-2 border-white relative transition-transform duration-200 ease-in-out `}
                  >
                    {project?.teams?.length - 5} +
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;
